/**
 * Every string RequestChooseScreen shows by default. A string marked CANDIDATE COPY is new copy
 * that is not approved: awaiting Charles's approval before merge (verifime-adr#617). A string
 * marked COPY APPROVED is the copy review wording of verifime-adr#683. An unmarked string is
 * reused as built (Cancel and Continue, from the entity type selector). The entity type names,
 * descriptions and help lines on the tiles are not here: they are the entity type tiles' own copy,
 * in entityTileContent.ts. The caller can replace any string below through the `copy` prop.
 */
import {
  CANCEL_LABEL,
  CONTINUE_LABEL,
} from '../../EntityTypeSelector/EntityTypeSelector/entityTypeSelectorCopy';

/**
 * What the primary button does, which the parent decides from the choice. The choose screen never
 * shares: every choice (an identity, a new one or a Draft) goes on to the confirm screen, so the
 * primary only continues (618 L5). `none` is the disabled button shown while nothing is chosen.
 */
export type RequestPrimaryAction = 'continue' | 'none';

export type RequestChooseScreenCopy = {
  /** The small label above the provider's name in the request header. */
  requestFrom: string;
  /** The label before what the request asks for, in the request header. */
  asksFor: string;
  /** What the request asks for when it accepts any non-individual type. The parent passes it as `asksLabel`. */
  anyNonIndividual: string;
  pageTitle: string;
  /** The one line under the title (desktop only). */
  intro: (providerName: string) => string;
  /** The heading over the rows of held identities. */
  heldHeading: string;
  /** The heading over the tiles, shown when held rows come before them. Also the tiles' group name. */
  orVerifyNew: string;
  /** The word before the chosen name beside the action. */
  selectedPrefix: string;
  /** The phone bar's line while nothing is chosen. */
  nothingChosenPrompt: string;
  primaryLabels: Record<RequestPrimaryAction, string>;
  cancel: string;
};

export const REQUEST_CHOOSE_SCREEN_COPY: RequestChooseScreenCopy = {
  // CANDIDATE COPY, not approved (verifime-adr#617)
  requestFrom: 'Request from',
  // CANDIDATE COPY, not approved (verifime-adr#617)
  asksFor: 'Asks for',
  // CANDIDATE COPY, not approved (verifime-adr#617)
  anyNonIndividual: 'Any non-individual',
  // CANDIDATE COPY, not approved (verifime-adr#617)
  pageTitle: 'Choose what to share',
  // CANDIDATE COPY, not approved (verifime-adr#617)
  intro: (providerName) => `${providerName} has asked for an identity. You choose which one.`,
  // CANDIDATE COPY, not approved (verifime-adr#617)
  heldHeading: 'What you already have, by type',
  // CANDIDATE COPY, not approved (verifime-adr#617)
  orVerifyNew: 'Or verify a new one',
  // CANDIDATE COPY, not approved (verifime-adr#617)
  selectedPrefix: 'Selected:',
  // CANDIDATE COPY, not approved (verifime-adr#617)
  nothingChosenPrompt: 'Choose an identity to continue',
  primaryLabels: {
    // As built: the entity type selector's Continue.
    continue: CONTINUE_LABEL,
    none: CONTINUE_LABEL,
  },
  // As built: the entity type selector's Cancel.
  cancel: CANCEL_LABEL,
};

/**
 * The footer note for a request that limits the choice (618 R6, Q4a). The parent passes it as
 * `limitNote` when the request limits the choice, and passes nothing when it does not, so the
 * screen has no default of its own.
 */
// COPY APPROVED (verifime-adr#683): Zoe McKeough, 2026-11-04
export const REQUEST_CHOOSE_LIMIT_NOTE =
  'If you do not have what your service provider asks for, or cannot give the information they need, go back to them.';

/**
 * Overrides for the default copy. Any string may be left out, including one action's label inside
 * `primaryLabels`. A string set to `undefined` counts as left out.
 */
export type RequestChooseScreenCopyOverrides = Partial<
  Omit<RequestChooseScreenCopy, 'primaryLabels'>
> & { primaryLabels?: Partial<RequestChooseScreenCopy['primaryLabels']> };

function withoutUndefined<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as Partial<T>;
}

/** The default copy with the caller's overrides laid over it, one string at a time. */
export function resolveRequestChooseScreenCopy(
  overrides: RequestChooseScreenCopyOverrides = {},
): RequestChooseScreenCopy {
  const { primaryLabels, ...strings } = overrides;
  return {
    ...REQUEST_CHOOSE_SCREEN_COPY,
    ...withoutUndefined(strings),
    primaryLabels: {
      ...REQUEST_CHOOSE_SCREEN_COPY.primaryLabels,
      ...withoutUndefined(primaryLabels ?? {}),
    },
  };
}
